// An invitation to install the app, shown where a device is being set up.
//
// It appears once and stays dismissed, because the person who sees it most is
// an operator signing in for a shift, and they are not the one who decides
// whether a tablet gets installed. A banner that returns every morning is a
// banner people learn to tap past.

import { useState } from 'react';
import { Icon, PodMark } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { shouldOfferInstall } from '@/lib/installPrompt';
import { todayKey } from '@/lib/milestone';
import { useStore } from '@/store';

export function InstallBanner() {
  const t = useT();
  const { method, install } = useInstallPrompt();
  const dismissedOn = useStore((s) => s.installDismissedOn);
  const setDismissed = useStore((s) => s.dismissInstall);
  const dismiss = () => setDismissed(todayKey());
  const [busy, setBusy] = useState(false);

  // A dismissal lapses rather than lasting forever — see INSTALL_REPROMPT_DAYS.
  if (!shouldOfferInstall(dismissedOn, todayKey()) || method === 'none') return null;

  const manual = method === 'ios-manual';

  return (
    <div
      role="region"
      aria-label={t.installTitle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        borderRadius: 16,
        background: 'var(--soft)',
        border: '1.5px solid var(--accent)',
        marginBottom: 14,
        animation: 'ipRise .3s ease-out',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: 'none',
          width: 38,
          height: 38,
          borderRadius: 12,
          background: 'var(--accent)',
          color: 'var(--accentInk)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <PodMark size={22} />
      </span>

      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: "700 13px 'Space Grotesk',sans-serif" }}>
          {t.installTitle}
        </span>
        <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
          {manual ? t.installIosHow : t.installWhy}
        </span>
      </span>

      {/* Nothing to trigger on iOS, so there is no button to offer — only the
          instructions above, which is the whole of what Safari allows. */}
      {!manual && (
        <button
          type="button"
          className="press"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const accepted = await install();
              // Declining is an answer. Asking again next time would be nagging.
              if (!accepted) dismiss();
            } finally {
              setBusy(false);
            }
          }}
          style={{
            flex: 'none',
            minHeight: 44,
            padding: '0 14px',
            borderRadius: 12,
            background: 'var(--accent)',
            color: 'var(--accentInk)',
            font: '700 12.5px Manrope,sans-serif',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {t.installAction}
        </button>
      )}

      <button
        type="button"
        className="press"
        onClick={() => dismiss()}
        aria-label={t.dismiss}
        style={{
          flex: 'none',
          width: 44,
          height: 44,
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          color: 'var(--sub)',
        }}
      >
        <Icon name="cross" size={16} />
      </button>
    </div>
  );
}
